import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { GetRequest } from 'components/common/index.js';

// Accept "5", "4.7", "0.5" — a single digit, optionally followed by a dot
// and one digit. Matches the .star_rating_X_Y CSS class set in custom_style.scss.
const isValidTrustpilotRating = (value) => {
  return typeof value === 'string' && /^\d(\.\d)?$/.test(value);
};

const PricingCards = ({ setMode, setSubscription, setCurrencyName, hideCredits, currencySelector }) => {
  const [subscriptionOneCredit, setSubscriptionOneCredit] = useState(undefined);
  // Starter / Business / Business Plus, each with its monthly and annual plan.
  const [tierSubscriptions, setTierSubscriptions] = useState({});
  // Billing period shared by the three tier cards; annual is the default (best value).
  const [annual, setAnnual] = useState(true);
  // Top-level Credit Packs / Subscriptions tabs, mirroring the pricing page.
  // Subscriptions is the default tab. When hideCredits is set there is only the
  // subscriptions view, so the tabs are not shown.
  const [activeTab, setActiveTab] = useState('subscriptions');
  // Which feature "learn more" popup is open on the Starter card (null when none).
  const [learnMoreModal, setLearnMoreModal] = useState(null);

  const fetchSubscriptions = () => {
    GetRequest({
      url: '/charge/subscriptions',
      callback: (data) => {
        setCurrencyName(data.currency_name);
        setSubscriptionOneCredit((_prev) => { return { ...data.one_credit }; });
        setTierSubscriptions(() => ({
          starter:       { monthly: { ...data.monthly_starter },       annually: { ...data.annually_starter } },
          business:      { monthly: { ...data.monthly_business },      annually: { ...data.annually_business } },
          business_plus: { monthly: { ...data.monthly_business_plus }, annually: { ...data.annually_business_plus } },
        }));
      },
    });
  };

  useEffect(fetchSubscriptions, []);
  useEffect(setUpManufacturingCostTooltip, []);

  useEffect(() => {
    const listen = 'show_drawing.currency_changed';
    document.addEventListener(listen, fetchSubscriptions);
    return () => { document.removeEventListener(listen, fetchSubscriptions); };
  }, []);

  // Initialize the countdown ticker on every .offer_ends element that the
  // newly-loaded subscription data renders. Tied to the fetched-data state so
  // this only re-runs when subscriptions arrive (mount + currency change) —
  // without the deps array the effect would fire on every render and stack
  // a new setInterval per .offer_ends element each time.
  useEffect(() => {
    $('.offer_ends').each(offerExpiresAt);
  }, [subscriptionOneCredit, tierSubscriptions, annual]);

  const handleCreditCardCTA = (e) => {
    e.preventDefault();
    if (subscriptionOneCredit === undefined) { return; }
    setSubscription(() => subscriptionOneCredit);
    setMode('payment_form');
  };

  const handleSubscriptionCardCTA = (subscription) => (e) => {
    e.preventDefault();
    setSubscription(() => subscription);
    setMode('payment_form');
  };

  // Whole-number annual saving vs paying monthly, derived from the Business tier
  // prices — the same "Save X%" badge the pricing page shows. Returns null until
  // the data has loaded or if it can't be computed.
  const annualSavingsPercent = () => {
    const business = tierSubscriptions.business;
    if (!business || !business.monthly || !business.annually) { return null; }
    const monthly = business.monthly.transaction_amount_in_pence;
    const annually = business.annually.transaction_amount_in_pence;
    if (!monthly || !annually) { return null; }
    const yearlyAtMonthlyRate = monthly * 12;
    if (yearlyAtMonthlyRate <= 0) { return null; }
    const percent = Math.round(((yearlyAtMonthlyRate - annually) / yearlyAtMonthlyRate) * 100);
    return percent > 0 ? percent : null;
  };

  // The headline price for a tier card. For annual billing this is the per-month
  // equivalent (the annual total is shown separately as "Billed … annually"); for
  // monthly billing it's the plain monthly price. The struck "old price" uses the
  // matching per-month / full figure when a discount applies.
  const renderSubscriptionPrice = (subscription, annualBilling) => {
    if (subscription === undefined) { return null; }
    const mainPrice = annualBilling ? subscription.price_per_month_formatted : subscription.price_formatted;
    const oldPrice = annualBilling
      ? subscription.price_per_month_without_discount_formatted
      : subscription.price_without_discount_formatted;
    if (subscription.has_discount) {
      return (
        <>
          <strike className="pricing-card-price-old">{oldPrice}</strike>
          <br />
          <span className="pricing-card-price-new">{mainPrice}</span>
        </>
      );
    }
    return <span className="pricing-card-price-new">{mainPrice}</span>;
  };

  const renderOfferBadge = (subscription) => {
    if (subscription === undefined || !subscription.has_discount) { return null; }
    return (
      <div className="pricing-card-offer">
        <span className="badge badge-dark">
          {subscription.coupon_code_expires_at != null ? (
            <span data-expires-at={subscription.coupon_code_expires_at} className="offer_ends" />
          ) : (
            t('sign_up.special_offer')
          )}
        </span>
      </div>
    );
  };

  const checkIcon = <i className="fa fa-check pricing-card-check" aria-hidden="true" />;

  const htmlFeature = (translationKey) => {
    return <span dangerouslySetInnerHTML={{ __html: t(translationKey) }} />;
  };
  const renderFeatureWithHtml = htmlFeature;

  // A Starter-card feature label followed by a small "Learn more" link that opens
  // its image popup (3D mockups, Illustrator plug-in), mirroring the pricing page.
  const featureWithLearnMore = (labelKey, modalKey) => (
    <>
      {/* The label may carry markup (e.g. sign_up.nesting is "<strong>Nesting</strong> …"),
          so render it as HTML like htmlFeature does. */}
      <span dangerouslySetInnerHTML={{ __html: t(labelKey) }} />{' '}
      <a
        href="#"
        className="feature-learn-more-link"
        onClick={(event) => { event.preventDefault(); setLearnMoreModal(modalKey); }}
      >
        {t('sign_up.feature_learn_more')}
      </a>
    </>
  );

  // Popup contents per feature: title, promo image (same asset as the home page
  // section), and the landing page it links to. The .feature-modal styles live in
  // custom_style.scss (shared with the pricing page).
  const learnMoreModals = {
    mockup: {
      title: t('sign_up.mockup_editor_title'),
      image: 'https://d2atdwxjx7uc4i.cloudfront.net/campaigns/3d_mockup.png',
      alt: t('three_d.create_three_d_design'),
      href: '/3d-mockups',
    },
    illustrator: {
      title: t('sign_up.illustrator_plugin_beta'),
      image: 'https://d2atdwxjx7uc4i.cloudfront.net/campaigns/plug-in-panel-sml.png',
      alt: t('illustrator_plugin_promo.image_alt'),
      href: '/tools/adobe-illustrator-plug-in',
    },
    // Nesting's promo image is a local asset (not on the CDN like the two above), so its
    // digested URL is passed in via window.nestingLearnMoreImageUrl (set in the layout).
    nesting: {
      title: t('nesting_promo.title'),
      image: window.nestingLearnMoreImageUrl,
      alt: t('nesting_promo.image_alt'),
      href: '/nesting-imposition',
    },
  };

  // Feature lists mirror the pricing page (home/plans/_subscriptions_tiers): each
  // higher tier is a superset of the one below it, so the middle/top cards lead
  // with "Everything in …" and list only their extras.
  const tierFeatures = {
    starter: [
      htmlFeature('sign_up.professional_dielines'),
      htmlFeature('sign_up.unlimited_support'),
      t('sign_up.unlimited_access'),
      htmlFeature('sign_up.unlimited_previews'),
      featureWithLearnMore('sign_up.mockup_editor_and_video_export', 'mockup'),
      featureWithLearnMore('sign_up.illustrator_plugin_beta', 'illustrator'),
      htmlFeature('sign_up.no_contract'),
      t('sign_up.renews_automatically'),
    ],
    business: [
      t('sign_up.everything_in_starter'),
      <strong>{t('sign_up.bounding_box_dimension_lines')}</strong>,
      featureWithLearnMore('sign_up.nesting', 'nesting'),
      <strong>{t('sign_up.manufacturing_cost_calculator')}</strong>,
      htmlFeature('sign_up.no_contract'),
      t('sign_up.renews_automatically'),
    ],
    business_plus: [
      t('sign_up.everything_in_business'),
      <strong>{t('sign_up.white_label')}</strong>,
      htmlFeature('sign_up.white_label_details'),
      htmlFeature('sign_up.no_contract'),
      t('sign_up.renews_automatically'),
    ],
  };

  const renderTierCard = ({ tierKey, title, features, featured }) => {
    const plans = tierSubscriptions[tierKey];
    const subscription = plans ? (annual ? plans.annually : plans.monthly) : undefined;
    // The headline is a monthly figure in both modes (annual shows the per-month
    // equivalent), so the period label is always "per month"; annual additionally
    // shows the annual total on a "Billed … annually" line beneath it.
    const periodLabel = t('sign_up.per_month');
    const billedAnnually =
      annual && subscription !== undefined && subscription.price_formatted !== undefined
        ? t('sign_up.billed_annually').replace('%{price}', subscription.price_formatted)
        : null;

    return (
      <div className={`pricing-card ${featured ? 'pricing-card-featured' : ''}`}>
        {featured && <div className="pricing-card-badge">{t('sign_up.most_popular')}</div>}
        <div className="pricing-card-header">
          <h4>{title}</h4>
        </div>

        <div className="pricing-card-features">
          <ul>
            {features.map((feature, index) => (
              <li key={index}>{checkIcon} {feature}</li>
            ))}
          </ul>
        </div>

        {renderOfferBadge(subscription)}

        <div className="pricing-card-price">
          {renderSubscriptionPrice(subscription, annual)}
          <div className="pricing-card-price-period">
            <small>{periodLabel}</small>
            {billedAnnually != null && (
              <small className="pricing-card-billed-annually">{billedAnnually}</small>
            )}
          </div>
        </div>

        <div className="pricing-card-cta">
          {subscription !== undefined && subscription.price_formatted !== undefined ? (
            <a className="btn simple-btn pricing-card-btn" href="#" onClick={handleSubscriptionCardCTA(subscription)}>
              {t('sign_up.get_started')}
            </a>
          ) : (
            <a className="btn simple-btn pricing-card-btn pricing-card-btn-disabled" href="#" onClick={(e) => e.preventDefault()}>
              {t('sign_up.please_wait')}
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="pricing-cards-container">
      <div className="pricing-cards-header">
        {hideCredits !== true && (
          <div className="pricing-cards-tabs">
            <button
              type="button"
              className={`pricing-cards-tab ${activeTab === 'credit_packs' ? 'active' : ''}`}
              onClick={() => setActiveTab('credit_packs')}
            >
              {t('sign_up.credit_packs')}
            </button>
            <button
              type="button"
              className={`pricing-cards-tab ${activeTab === 'subscriptions' ? 'active' : ''}`}
              onClick={() => setActiveTab('subscriptions')}
            >
              {t('sign_up.subscriptions')}
            </button>
          </div>
        )}

        {/* Currency sits top-right; the monthly/annual toggle stacks directly
            beneath it, right-aligned, while the tabs stay centred. */}
        <div className="pricing-cards-header-right">
          {currencySelector && (
            <div className="pricing-cards-currency">{currencySelector}</div>
          )}

          {(hideCredits === true || activeTab === 'subscriptions') && (
            <div className="billing-toggle-global">
              <span className="toggle-label" style={{ fontWeight: annual ? 'normal' : 'bold' }} onClick={() => setAnnual(false)}>{t('sign_up.billing_monthly')}</span>
              <label className="switch">
                <input type="checkbox" checked={annual} onChange={(event) => setAnnual(event.target.checked)} />
                <span className="slider round"></span>
              </label>
              <span className="toggle-label" style={{ fontWeight: annual ? 'bold' : 'normal' }} onClick={() => setAnnual(true)}>
                {t('sign_up.billing_annually')}
              </span>
              {annual && annualSavingsPercent() != null && (
                <span className="badge badge-dark pricing-cards-save-badge">
                  {t('sign_up.save_percentage').replace('%{percentage}', annualSavingsPercent())}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {(hideCredits === true || activeTab === 'subscriptions') && (
        <div className="pricing-cards-grid pricing-cards-grid-tiers">
          {renderTierCard({ tierKey: 'starter', title: t('sign_up.tier_starter'), features: tierFeatures.starter, featured: false })}
          {renderTierCard({ tierKey: 'business', title: t('sign_up.tier_business'), features: tierFeatures.business, featured: true })}
          {renderTierCard({ tierKey: 'business_plus', title: t('sign_up.tier_business_plus'), features: tierFeatures.business_plus, featured: false })}
        </div>
      )}

      {hideCredits !== true && activeTab === 'credit_packs' && (
        <div className="pricing-cards-grid">
          <div className="pricing-card pricing-card-credits-solo">
            <div className="pricing-card-header">
              <h4>{t('sign_up.credits')}</h4>
            </div>

            <div className="pricing-card-features">
              <ul>
                <li>{checkIcon} {renderFeatureWithHtml('sign_up.professional_dielines')}</li>
                <li>{checkIcon} {t('sign_up.mockup_editor_and_video_export')}</li>
                <li>{checkIcon} {t('sign_up.credit_valid_for_48_hours')}</li>
              </ul>
            </div>

            {renderOfferBadge(subscriptionOneCredit)}

            <div className="pricing-card-price">
              {renderSubscriptionPrice(subscriptionOneCredit)}
            </div>

            <div className="pricing-card-cta">
              {subscriptionOneCredit !== undefined ? (
                <a className="btn simple-btn pricing-card-btn" href="#" onClick={handleCreditCardCTA}>
                  {t('sign_up.purchase_one_credit')}
                </a>
              ) : (
                <a className="btn simple-btn pricing-card-btn pricing-card-btn-disabled" href="#" onClick={(e) => e.preventDefault()}>
                  {t('sign_up.please_wait')}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {window.TrustPilotEnabled && isValidTrustpilotRating(window.TrustpilotRating) && (
        <a
          className="pricing-cards-trust-signal"
          href="/out?key=all_reviews"
          target="_blank"
          rel="noopener"
        >
          <div className="pricing-cards-trust-signal-stars">
            <div
              className={`star_rating_${window.TrustpilotRating.replace('.', '_')}`}
              role="img"
              aria-label={t('sign_up.trustpilot_rated_out_of_five').replace('%{rating}', window.TrustpilotRating)}
            ></div>
          </div>
          <span className="pricing-cards-trust-signal-text">
            {t('sign_up.trustpilot_rated_out_of_five').replace('%{rating}', window.TrustpilotRating)} &mdash; {t('sign_up.trustpilot_reviews_count')}
          </span>
        </a>
      )}

      {learnMoreModal && createPortal(
        // Rendered to <body> so the fixed overlay clears the paywall's own modal.
        // stopPropagation keeps clicks from bubbling through the React portal tree
        // back into the paywall (which would otherwise risk closing it).
        <div className="feature-modal is-open" onClick={(event) => event.stopPropagation()}>
          <div className="feature-modal-backdrop" onClick={() => setLearnMoreModal(null)}></div>
          <div className="feature-modal-dialog" role="dialog" aria-modal="true" aria-label={learnMoreModals[learnMoreModal].title}>
            <div className="feature-modal-header">
              <h3 className="feature-modal-title">{learnMoreModals[learnMoreModal].title}</h3>
              <a href="#" className="feature-modal-close-x" aria-label={t('sign_up.multi_seat_close')} onClick={(event) => { event.preventDefault(); setLearnMoreModal(null); }}>&times;</a>
            </div>
            <a href={learnMoreModals[learnMoreModal].href} target="_blank" rel="noopener">
              <img className="feature-modal-image" src={learnMoreModals[learnMoreModal].image} alt={learnMoreModals[learnMoreModal].alt} />
            </a>
            <div className="feature-modal-footer">
              <a href={learnMoreModals[learnMoreModal].href} target="_blank" rel="noopener" className="feature-modal-learn-more">{t('sign_up.feature_learn_more')}</a>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default PricingCards;
